/**
 * Stats for the "wrapped" recap of one month (or a whole year): games played,
 * most played title, biggest climber, upset, most active player and your own period.
 *
 * Everything is computed client-side from the plays and the Global rating
 * histories, so the recap needs no backend changes. Any stat the period is too
 * thin for comes back null and its card is simply left out.
 */
import { PlayOutput, RatingHistory } from '../generated-api';

export type WrapPeriod =
  | { kind: 'month'; year: number; month: number } // month: 1-12
  | { kind: 'year'; year: number };

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Rating defaults and team model, same as the backend (config.py, services/ratings.py)
const DEFAULT_MU = 25;
const DEFAULT_SIGMA = 8.333;
const BETA = 5;
const TEAM_SIZE_BONUS = 0.1;

// Same certainty threshold as the league table: 5+ games and sigma at most 5.
const MIN_GAMES_FOR_RANK = 5;
const MAX_SIGMA_FOR_RANK = 5;

/** A win counts as an upset when the winners' chance was below this. */
export const UPSET_MAX_PROBABILITY = 0.5;

/** Local-time [start, end) of the period. */
export const periodRange = (p: WrapPeriod): { start: Date; end: Date } =>
  p.kind === 'month'
    ? { start: new Date(p.year, p.month - 1, 1), end: new Date(p.year, p.month, 1) }
    : { start: new Date(p.year, 0, 1), end: new Date(p.year + 1, 0, 1) };

/** "October", "October 2025" (another year than now's) or "2026". */
export const periodLabel = (p: WrapPeriod, now: Date = new Date()): string => {
  if (p.kind === 'year') return String(p.year);
  const name = MONTHS[p.month - 1];
  return p.year === now.getFullYear() ? name : `${name} ${p.year}`;
};

/** URL form: "2026-10" or "2026". */
export const periodKey = (p: WrapPeriod): string =>
  p.kind === 'year' ? String(p.year) : `${p.year}-${String(p.month).padStart(2, '0')}`;

/** The last finished month: what the recap shows by default. */
export const lastMonth = (now: Date = new Date()): WrapPeriod => {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { kind: 'month', year: d.getFullYear(), month: d.getMonth() + 1 };
};

/** Parses periodKey's output; anything else falls back to last month. */
export const parsePeriod = (key: string | undefined, now: Date = new Date()): WrapPeriod => {
  const month = key?.match(/^(\d{4})-(\d{2})$/);
  if (month && +month[2] >= 1 && +month[2] <= 12) {
    return { kind: 'month', year: +month[1], month: +month[2] };
  }
  const year = key?.match(/^(\d{4})$/);
  if (year) return { kind: 'year', year: +year[1] };
  return lastMonth(now);
};

/** The period before (-1) or after (+1) this one, of the same kind. */
export const shiftPeriod = (p: WrapPeriod, delta: number): WrapPeriod => {
  if (p.kind === 'year') return { kind: 'year', year: p.year + delta };
  const d = new Date(p.year, p.month - 1 + delta, 1);
  return { kind: 'month', year: d.getFullYear(), month: d.getMonth() + 1 };
};

/** True once the period has started (a recap of the future is empty). */
export const periodHasStarted = (p: WrapPeriod, now: Date = new Date()): boolean =>
  periodRange(p).start.getTime() <= now.getTime();

/**
 * Recaps worth pointing at from the Home page right now: last month during the
 * first week of a month, and the year from Dec 20 until Jan 7.
 */
export const featuredPeriods = (now: Date = new Date()): WrapPeriod[] => {
  const result: WrapPeriod[] = [];
  const month = now.getMonth();
  const day = now.getDate();
  if (month === 11 && day >= 20) result.push({ kind: 'year', year: now.getFullYear() });
  if (month === 0 && day <= 7) result.push({ kind: 'year', year: now.getFullYear() - 1 });
  if (day <= 7) result.push(lastMonth(now));
  return result;
};

const inRange = (date: string, start: Date, end: Date) => {
  const t = new Date(date).getTime();
  return t >= start.getTime() && t < end.getTime();
};

/** Team names that won the play (first place; ties all win). */
export const winningTeams = (play: PlayOutput): string[] => {
  const first = play.ranking?.[0];
  if (first === undefined) return [];
  return Array.isArray(first) ? first : [first];
};

const erf = (x: number) => {
  // Abramowitz-Stegun 7.1.26, |error| < 1.5e-7
  const sign = x < 0 ? -1 : 1;
  const a = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * a);
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-a * a);
  return sign * y;
};

/**
 * Chance each team finishes first, as the backend's calculate_win_probability:
 * a team is one virtual player (strongest member + 10% per extra member, the
 * strongest member's sigma) performing N(mu, sigma^2 + beta^2).
 */
export const winProbabilities = (teams: { mu: number; sigma: number }[][]): number[] => {
  if (teams.length < 2) return teams.map(() => 1);
  const perf = teams.map(team => {
    const leader = team.reduce((a, b) => (b.mu > a.mu ? b : a));
    const mu = leader.mu * (1 + TEAM_SIZE_BONUS * (team.length - 1));
    return { mu, sd: Math.sqrt(leader.sigma ** 2 + BETA ** 2) };
  });
  const lo = Math.min(...perf.map(p => p.mu - 8 * p.sd));
  const hi = Math.max(...perf.map(p => p.mu + 8 * p.sd));
  const steps = 600;
  const dx = (hi - lo) / steps;
  const pdf = (x: number, mu: number, sd: number) =>
    Math.exp(-0.5 * ((x - mu) / sd) ** 2) / (sd * Math.sqrt(2 * Math.PI));
  const cdf = (x: number, mu: number, sd: number) => 0.5 * (1 + erf((x - mu) / (sd * Math.SQRT2)));
  const probs = perf.map(() => 0);
  for (let k = 0; k < steps; k++) {
    const x = lo + (k + 0.5) * dx;
    const cdfs = perf.map(p => cdf(x, p.mu, p.sd));
    perf.forEach((p, i) => {
      let others = 1;
      cdfs.forEach((c, j) => {
        if (j !== i) others *= c;
      });
      probs[i] += pdf(x, p.mu, p.sd) * others * dx;
    });
  }
  const total = probs.reduce((a, b) => a + b, 0) || 1;
  return probs.map(p => p / total);
};

/** Global position of every certain player using ratings from plays before `cutoff`. */
export const standingsBefore = (histories: RatingHistory[], cutoff: Date): Map<string, number> => {
  const ranked: { playerId: string; conservative: number }[] = [];
  for (const h of histories) {
    const entries = (h.ratingProgression || []).filter(e => new Date(e.playDate).getTime() < cutoff.getTime());
    const last = entries[entries.length - 1];
    if (!last) continue;
    if (entries.length < MIN_GAMES_FOR_RANK || last.ratingAfter.sigma > MAX_SIGMA_FOR_RANK) continue;
    ranked.push({ playerId: h.playerId, conservative: last.ratingAfter.rating - 3 * last.ratingAfter.sigma });
  }
  ranked.sort((a, b) => b.conservative - a.conservative);
  return new Map(ranked.map((r, i) => [r.playerId, i + 1]));
};

export interface WrappedStats {
  playCount: number;
  titleCount: number;
  /** Players who played at least once. */
  playerCount: number;
  /** Every title played, most played first (ties by name). */
  titles: { gameId: string; name: string; count: number }[];
  /** Every player who played, most plays first (ties by name). */
  players: { playerId: string; name: string; count: number }[];
  mostPlayed: { gameId: string; name: string; count: number } | null;
  /** Largest rating (mu) gain over the period; null when nobody gained. */
  biggestClimber: { playerId: string; name: string; delta: number } | null;
  /** Win with the lowest pre-play chance, if any winner was an underdog. */
  upset: {
    playId: string;
    gameId: string;
    gameName: string;
    winnerIds: string[];
    winnerNames: string[];
    probability: number;
    /** First photo attached to the play, if any. */
    photoUrl: string | null;
  } | null;
  /** The most active player and the titles they played most (up to 3). */
  mostGames: { playerId: string; name: string; count: number; topGameIds: string[] } | null;
  /** Null when there is no signed-in player or they didn't play. */
  me: {
    playerId: string;
    name: string;
    plays: number;
    wins: number;
    delta: number;
    rankBefore: number | null;
    rankAfter: number | null;
    bestGame: { gameId: string; name: string; wins: number } | null;
  } | null;
}

interface ComputeInput {
  period: WrapPeriod;
  plays: PlayOutput[];
  histories: RatingHistory[];
  playerNames: Record<string, string>;
  gameNames: Record<string, string>;
  meId?: string | null;
}

/** Highest count; ties go to the alphabetically first name so the result is stable. */
const byCount = <T extends { count: number; name: string }>(a: T, b: T) =>
  b.count - a.count || a.name.localeCompare(b.name);

const top = <T extends { count: number; name: string }>(items: T[]): T | null =>
  items.length === 0 ? null : [...items].sort(byCount)[0];

export const computeWrapped = ({ period, plays, histories, playerNames, gameNames, meId }: ComputeInput): WrappedStats => {
  const { start, end } = periodRange(period);
  const periodPlays = plays.filter(p => inRange(p.playDate, start, end));
  const playerName = (id: string) => playerNames[id] || 'Someone';
  const gameName = (id: string) => gameNames[id] || 'a game';

  const gameCounts = new Map<string, number>();
  const playerCounts = new Map<string, number>();
  for (const play of periodPlays) {
    gameCounts.set(play.gameId, (gameCounts.get(play.gameId) || 0) + 1);
    const ids = new Set(Object.values(play.teams || {}).flat());
    ids.forEach(id => playerCounts.set(id, (playerCounts.get(id) || 0) + 1));
  }

  const titles = Array.from(gameCounts, ([gameId, count]) => ({ gameId, name: gameName(gameId), count })).sort(byCount);
  const players = Array.from(playerCounts, ([playerId, count]) => ({ playerId, name: playerName(playerId), count })).sort(byCount);
  const mostPlayed = titles[0] || null;
  let mostGames: WrappedStats['mostGames'] = null;
  if (players[0]) {
    const theirGames = new Map<string, number>();
    for (const play of periodPlays) {
      if (Object.values(play.teams || {}).some(ids => ids.includes(players[0].playerId))) {
        theirGames.set(play.gameId, (theirGames.get(play.gameId) || 0) + 1);
      }
    }
    const topGameIds = Array.from(theirGames, ([gameId, count]) => ({ gameId, name: gameName(gameId), count }))
      .sort(byCount)
      .slice(0, 3)
      .map(g => g.gameId);
    mostGames = { ...players[0], topGameIds };
  }

  // Rating change over the period and the rating everyone had before each play
  const deltas = new Map<string, number>();
  const ratingBefore = new Map<string, Map<string, { mu: number; sigma: number }>>(); // playId -> player -> rating
  for (const h of histories) {
    let previous = { mu: DEFAULT_MU, sigma: DEFAULT_SIGMA };
    for (const e of h.ratingProgression || []) {
      if (inRange(e.playDate, start, end)) {
        deltas.set(h.playerId, (deltas.get(h.playerId) || 0) + e.ratingChange);
        if (!ratingBefore.has(e.playId)) ratingBefore.set(e.playId, new Map());
        ratingBefore.get(e.playId)!.set(h.playerId, previous);
      }
      previous = { mu: e.ratingAfter.rating, sigma: e.ratingAfter.sigma };
    }
  }

  let biggestClimber: WrappedStats['biggestClimber'] = null;
  deltas.forEach((delta, playerId) => {
    if (delta > 0 && (!biggestClimber || delta > biggestClimber.delta)) {
      biggestClimber = { playerId, name: playerName(playerId), delta };
    }
  });

  let upset: WrappedStats['upset'] = null;
  for (const play of periodPlays) {
    const winners = winningTeams(play);
    const teamNames = Object.keys(play.teams || {}).filter(t => (play.teams[t] || []).length > 0);
    if (winners.length !== 1 || teamNames.length < 2) continue;
    const before = ratingBefore.get(play.id);
    const teams = teamNames.map(t =>
      play.teams[t].map(id => before?.get(id) || { mu: DEFAULT_MU, sigma: DEFAULT_SIGMA }),
    );
    const probability = winProbabilities(teams)[teamNames.indexOf(winners[0])];
    if (probability < UPSET_MAX_PROBABILITY && (!upset || probability < upset.probability)) {
      upset = {
        playId: play.id,
        gameId: play.gameId,
        gameName: gameName(play.gameId),
        winnerIds: play.teams[winners[0]],
        winnerNames: play.teams[winners[0]].map(playerName),
        probability,
        photoUrl: play.imageUrls?.[0] || null,
      };
    }
  }

  let me: WrappedStats['me'] = null;
  if (meId && playerCounts.has(meId)) {
    const winsByGame = new Map<string, number>();
    const playsByGame = new Map<string, number>();
    let wins = 0;
    for (const play of periodPlays) {
      const myTeam = Object.keys(play.teams || {}).find(t => play.teams[t].includes(meId));
      if (!myTeam) continue;
      playsByGame.set(play.gameId, (playsByGame.get(play.gameId) || 0) + 1);
      if (winningTeams(play).includes(myTeam)) {
        wins += 1;
        winsByGame.set(play.gameId, (winsByGame.get(play.gameId) || 0) + 1);
      }
    }
    const best = top(Array.from(winsByGame, ([gameId, count]) => ({ gameId, name: gameName(gameId), count })));
    me = {
      playerId: meId,
      name: playerName(meId),
      plays: playerCounts.get(meId)!,
      wins,
      delta: deltas.get(meId) || 0,
      rankBefore: standingsBefore(histories, start).get(meId) ?? null,
      rankAfter: standingsBefore(histories, end).get(meId) ?? null,
      bestGame: best ? { gameId: best.gameId, name: best.name, wins: best.count } : null,
    };
  }

  return {
    playCount: periodPlays.length,
    titleCount: gameCounts.size,
    playerCount: playerCounts.size,
    titles,
    players,
    mostPlayed,
    biggestClimber,
    upset,
    mostGames,
    me,
  };
};
